import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { Schedule } from '@mui/icons-material';
import authApi from '../services/authApi';
import { useUserSession } from '../contexts/UserContext';
import axios from 'axios';
import ScheduleManager from './SecurityAudit/ScheduleManager';
import { saveAs } from 'file-saver';
import NavigationDrawer from './Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';
import { useSharePoint } from '../contexts/SharePointContext';
import { useFetchGeneration } from '../hooks/useFetchGeneration';
import { sharePointService } from '../services/sharePointService';

const SecurityAuditReportDW = () => {
  const [lists, setLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const { userSession } = useUserSession();
  const { effectiveConfigId, refreshTrigger } = useSharePoint();
  const { begin, isCurrent } = useFetchGeneration();
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedLists([]);
    setLists([]);
    if (!userSession?.email || !effectiveConfigId) return;

    const fetchGen = begin();
    const fetchSharePointLists = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = authApi.getToken();
        const rows = await sharePointService.getDataWarehouseLists(token, effectiveConfigId);
        if (!isCurrent(fetchGen)) return;
        const libs = (rows || [])
          .filter((r) => r.container_type === 'library')
          .map((r) => ({ id: r.id, name: r.title }));
        setLists(libs);
      } catch (err) {
        if (isCurrent(fetchGen)) {
          console.error('Error fetching SharePoint lists:', err);
          setError('Failed to load document libraries');
        }
      } finally {
        if (isCurrent(fetchGen)) {
          setLoading(false);
        }
      }
    };
    fetchSharePointLists();
  }, [userSession?.email, effectiveConfigId, refreshTrigger, begin, isCurrent]);

  const handleGenerateReport = async () => {
    if (selectedLists.length === 0 || generating) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const token = authApi.getToken();
      const allPermissions = [];

      for (const listId of selectedLists) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/datawarehouse/security-report`,
            { 
              listId,
              configId: effectiveConfigId 
            },
            {
              headers: { 
                Authorization: `Bearer ${token}`,
              }
            }
          );

          if (response.data?.success) {
            const responseData = response.data.data;
            if (responseData && responseData.length > 0) {
                allPermissions.push({
                    data: responseData,
                    listId
                });
            } else {
                console.warn(`No permissions data found for list: ${listId}`);
            }
          }
        } catch (error) {
          console.error('Error getting permissions for list:', listId, error);
        }
      }

      if (allPermissions.length > 0) {
        try {
          if (sendEmail) {
            await axios.post(
              `${process.env.REACT_APP_API_URL}/api/send-report`,
              {
                userEmail: userSession?.email,
                reportContent: allPermissions,
                fileName: `SharePoint-Security-Report-DW-${new Date().toLocaleDateString()}.xlsx`,
                source: 'datawarehouse',
                configId: effectiveConfigId
              },
              {
                headers: { 
                  Authorization: `Bearer ${token}`,
                }
              }
            );
          } else {
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/datawarehouse/security-report/download`,
              { 
                reportContent: allPermissions,
                configId: effectiveConfigId 
              },
              {
                headers: { 
                  Authorization: `Bearer ${token}`,
                },
                responseType: 'blob'
              }
            );

            const blob = new Blob([response.data], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, `SharePoint-Security-Report-DW-${new Date().toLocaleDateString()}.xlsx`);
          }
        } catch (error) {
          console.error('Error generating final report:', error);
          throw error;
        }
      } else {
        throw new Error('No permissions data found for any selected lists');
      }
    } catch (error) {
      console.error('Error in report generation:', error);
      setError(error.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Security Audit Report - Data Warehouse
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(244, 81, 30, 0.06)' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            What this report is for
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
            This report exports <strong>detailed permission information</strong> for files and folders in the document
            library you choose, using data already loaded into the data warehouse from SharePoint. Use it for audits,
            access reviews, and evidence of who can read or change content.
          </Typography>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            How to run it
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">
              Pick the correct site in the header, then choose a <strong>library</strong> from the list (libraries come
              from the warehouse; run ETL first if the list is empty).
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Generate Report</strong> builds an Excel workbook. Leave <strong>Send report via email</strong>{' '}
              off to download in the browser, or turn it on to email the workbook to yourself.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Manage Report Schedules</strong> opens scheduling for recurring runs of this style of report.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select SharePoint Lists/Libraries</InputLabel>
            <Select
              value={selectedLists[0] || ""}
              onChange={(e) => setSelectedLists([e.target.value])}
              disabled={loading}
            >
              {lists.map((library) => (
                <MenuItem key={library.id} value={library.id}>
                  {library.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!loading && lists.length === 0 && effectiveConfigId ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No document libraries found for this site in the warehouse. Confirm ETL has synced libraries for configuration {effectiveConfigId}.
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label="Send report via email"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={selectedLists.length === 0 || generating}
            startIcon={generating ? <CircularProgress size={20} /> : null}
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </Button>

          <Button
            variant="outlined"
            onClick={() => setShowScheduleManager(true)}
            startIcon={<Schedule />}
          >
            Manage Report Schedules
          </Button>
        </Box>

        <Dialog
          open={showScheduleManager}
          onClose={() => setShowScheduleManager(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Manage Report Schedules</DialogTitle>
          <DialogContent>
            <ScheduleManager 
              selectedLists={selectedLists}
              onSelectedListsChange={setSelectedLists}
              lists={lists}
              onClose={() => setShowScheduleManager(false)}
              source="datawarehouse"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowScheduleManager(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
        >
          <Alert 
            onClose={() => setError(null)} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default SecurityAuditReportDW; 